import { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import { libraryState } from "./libraryStore";
import { identifyClient, track, TRACK_EVENT } from "./analytics";
import { watchApi } from "./apiStatus";

// Session of a client (buyer) — a media company member using the Video
// Library. Separate from the staff/creator login (vb-access-token):
// own cookie, own axios instance (no staff refresh/redirect interceptors),
// token sent as X-Client-Token.

const COOKIE = "vb-client-token";
const SESSION_EVENT = "vb-client-session-change";

export const clientApi = axios.create({
  baseURL: process.env.REACT_APP_SERVER_BASE_URL,
  timeout: 60000,
});
watchApi(clientApi); // backend updates: banner + GET retries

clientApi.interceptors.request.use((request) => {
  const token = Cookies.get(COOKIE);
  if (token) request.headers["X-Client-Token"] = token;
  return request;
});

clientApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.data?.code === "CLIENT_AUTH") clearSession();
    return Promise.reject(err);
  }
);

let session = null; // { member, company }
let loading = !!Cookies.get(COOKIE);

const emit = () => window.dispatchEvent(new Event(SESSION_EVENT));

const setToken = (token) =>
  Cookies.set(COOKIE, token, {
    expires: 7,
    sameSite: "lax",
    secure: window.location.protocol === "https:",
  });

function clearSession() {
  Cookies.remove(COOKIE);
  session = null;
  loading = false;
  stopSync();
  emit();
}

/* ─── Sync of collections / saved searches ───────── */

let syncTimer = null;
let unsubscribe = null;

const pushState = () => {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    clientApi.put("/client/library-state", libraryState.export()).catch(() => {});
  }, 1200);
};

const startSync = async () => {
  try {
    const { data } = await clientApi.get("/client/library-state");
    const remote = data?.apiData;
    const hasRemote = remote && (remote.collections?.length || remote.savedSearches?.length);
    if (hasRemote) libraryState.import(remote);
    else pushState(); // first sign-in on this account: keep what's in the browser
  } catch (e) {}
  stopSync();
  unsubscribe = libraryState.subscribe(pushState);
};

function stopSync() {
  clearTimeout(syncTimer);
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
}

const onSignedIn = (apiData) => {
  setToken(apiData.token);
  session = { member: apiData.member, company: apiData.company };
  loading = false;
  identifyClient(session);
  emit();
  startSync();
  return session;
};

/* ─── Public API ─────────────────────────────────── */

export const clientAuth = {
  login: async (email, password) => {
    const { data } = await clientApi.post("/client/auth/login", { email, password });
    const out = onSignedIn(data.apiData);
    track("login", { method: "client_account" });
    return out;
  },
  inviteInfo: async (token) => {
    const { data } = await clientApi.get(`/client/auth/invite/${encodeURIComponent(token)}`);
    return data.apiData;
  },
  acceptInvite: async ({ token, name, password }) => {
    const { data } = await clientApi.post("/client/auth/invite/accept", { token, name, password });
    const out = onSignedIn(data.apiData);
    // the invited member creates their account
    track("sign_up", { method: "client_invite" });
    return out;
  },
  forgot: async (email) => {
    const { data } = await clientApi.post("/client/auth/forgot", { email });
    return data.message;
  },
  resetPassword: async ({ token, password }) => {
    const { data } = await clientApi.post("/client/auth/reset", { token, password });
    return onSignedIn(data.apiData);
  },
  updateProfile: async ({ name, jobTitle }) => {
    const { data } = await clientApi.patch("/client/me", { name, jobTitle });
    session = data.apiData;
    emit();
    return session;
  },
  changePassword: async ({ current, next }) => {
    const { data } = await clientApi.post("/client/me/password", { current, next });
    return data.message;
  },
  logout: () => {
    clearSession();
    libraryState.reset();
  },
  token: () => Cookies.get(COOKIE),
};

// Restores the session from the cookie once per page load
let restored = false;
const restore = async () => {
  if (restored) return;
  restored = true;
  if (!Cookies.get(COOKIE)) {
    loading = false;
    emit();
    return;
  }
  try {
    const { data } = await clientApi.get("/client/me");
    session = data.apiData;
    identifyClient(session);
    loading = false;
    emit();
    startSync();
  } catch (e) {
    clearSession();
  }
};

export const useClientSession = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const sync = () => force((n) => n + 1);
    window.addEventListener(SESSION_EVENT, sync);
    restore();
    return () => window.removeEventListener(SESSION_EVENT, sync);
  }, []);
  return {
    member: session?.member || null,
    company: session?.company || null,
    signedIn: !!session,
    loading,
  };
};

// The single login page (company tab); after signing in → back to `next`
export const companyLoginUrl = (next = window.location.pathname + window.location.search) =>
  `/login?as=company&next=${encodeURIComponent(next)}`;

// "Sarah Kent" → "SK"
export const initials = (name, email) => {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

export const errorMessage = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message || fallback;

/* ─── Activity for admin → Clients ───────────────── */

// While a client is signed in: the pages they open, what they do with clips
// (the same events Google Analytics gets) and a ping a minute while the tab
// is visible and in use.
const ACTIVITY = {
  page_view: (p) => ({ text: p.path }),
  video_view: (p) => ({ videoId: p.video_id }),
  video_search: (p) => ({ text: p.search_term }),
  video_download: (p) => ({ videoId: p.video_id, text: p.source }),
  license_click: (p) => ({ videoId: p.video_id }),
  license_request: (p) => ({ videoId: String(p.video_id || "").split(",")[0], text: p.usage || p.method }),
  footage_request: (p) => ({ text: p.topics || p.topic || "" }),
  clip_question: (p) => ({ videoId: p.video_id }),
};
// the first page fires before the session has loaded: it waits for it
let waiting = [];
const sendActivity = (kind, extra = {}) => {
  if (!session) {
    if (loading && waiting.length < 10) waiting.push([kind, extra]);
    return;
  }
  clientApi.post("/client/activity", { kind, ...extra }).catch(() => {});
};
if (typeof window !== "undefined") {
  window.addEventListener(SESSION_EVENT, () => {
    const list = waiting;
    waiting = [];
    if (session) list.forEach(([kind, extra]) => sendActivity(kind, extra));
  });
  window.addEventListener(TRACK_EVENT, (e) => {
    const { name, params } = e.detail || {};
    if (ACTIVITY[name]) sendActivity(name, ACTIVITY[name](params || {}));
  });
  let lastInput = Date.now();
  ["mousemove", "keydown", "scroll", "touchstart", "click"].forEach((ev) =>
    window.addEventListener(ev, () => (lastInput = Date.now()), { passive: true })
  );
  setInterval(() => {
    if (document.visibilityState === "visible" && Date.now() - lastInput < 10 * 60e3) sendActivity("ping");
  }, 60e3);
}
